import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Container,
  Card,
  Form,
  Row,
  Col,
  Button,
  Badge,
  Accordion,
  ProgressBar,
} from "react-bootstrap";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import {
  Save,
  CornerDownLeft,
  ShieldCheck,
  CheckCircle2,
  CircleDot,
  AlertTriangle,
} from "lucide-react";

import AlertMessage from "../../components/Common/AlertMessage";
import extractApiErrors from "../../utils/extractApiErrors";

// Selects de catálogo genérico (Category / CategoryItem)
import SelectCategory from "../../components/Common/SelectCategory";

// Selects de entidades (ajusta la ruta/nombre si lo llamas diferente)
import SelectCustomer from "../../components/Common/SelectCustomer";
import SelectLawyer from "../../components/Common/SelectLawyer";

import {
  createCase,
  updateCase,
  getCaseById,
} from "../../services/caseService";

import { getCategoryByCode } from "../../services/categoryService";

// ------------------------------------- UI: Sticky Header -------------------------------------
/**
 * Encabezado fijo superior:
 * - Título dinámico (Crear / Editar caso)
 * - Botón Volver
 * - Botón Guardar
 * - Barra de progreso según campos obligatorios diligenciados
 */
const StickyHeader = ({ title, onBack, onSave, disabled, progress }) => (
  <div className="bg-body border-bottom position-sticky top-0 z-3">
    <Container className="py-3">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div className="min-w-0">
          <h3 className="mb-0 text-truncate">{title}</h3>
          <div className="text-muted small mt-1">
            Completa los campos requeridos y guarda el caso.
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="secondary"
            onClick={onBack}
            className="d-flex align-items-center gap-2"
          >
            <CornerDownLeft size={16} /> Volver
          </Button>
          <Button
            onClick={onSave}
            disabled={disabled}
            className="d-flex align-items-center gap-2"
          >
            <Save size={16} /> Guardar
          </Button>
        </div>
      </div>
      <div className="mt-3">
        <ProgressBar now={progress} style={{ height: 6 }} />
      </div>
    </Container>
  </div>
);

// ------------------------------------- Constantes de categorías -------------------------------------
// Ajusta estos códigos a los que uses en Category/CategoryItem en backend
const CASE_TYPE_CATEGORY_CODE = "case_type";
const CASE_STATUS_CATEGORY_CODE = "case_status";
const CASE_PRIORITY_CATEGORY_CODE = "case_priority";
const CASE_JURISDICTION_CATEGORY_CODE = "jurisdiction";
const CASE_COURT_CATEGORY_CODE = "court";

// ------------------------------------- Validación Yup -------------------------------------
/**
 * Esquema de validación para creación/edición de Caso.
 * - En creación: la fecha límite no puede ser anterior a "ahora".
 * - En edición: permitimos fechas pasadas (el caso ya quedó así en su historia).
 */
const buildValidationSchema = (isCreate) =>
  Yup.object().shape({
    title: Yup.string()
      .required("El título del caso es obligatorio.")
      .max(200, "Máximo 200 caracteres.")
      .test("trim", "Sin espacios al inicio/fin.", (v) =>
        typeof v === "string" ? v === v.trim() : true
      ),

    description: Yup.string()
      .required("La descripción es obligatoria.")
      .min(50, "Debe contener al menos 50 caracteres.")
      .max(2000, "Máximo 2000 caracteres.")
      .test(
        "trim",
        "La descripción no puede iniciar o terminar con espacios.",
        (value) => (value ?? "") === value?.trim()
      )
      .test(
        "not-only-spaces",
        "La descripción no puede contener solo espacios.",
        (value) => !!value && value.trim().length > 0
      ),

    customerId: Yup.string()
      .uuid("Cliente inválido")
      .required("El cliente es obligatorio."),

    lawyerResponsibleId: Yup.string()
      .uuid("Abogado inválido")
      .required("El abogado responsable es obligatorio."),

    caseTypeId: Yup.string()
      .uuid("Tipo de caso inválido")
      .required("El tipo de caso es obligatorio."),

    caseStatusId: Yup.string()
      .uuid("Estado de caso inválido")
      .required("El estado del caso es obligatorio."),

    jurisdictionId: Yup.string()
      .nullable()
      .notRequired()
      .test("guid-or-empty", "Jurisdicción inválida.", (v) =>
        !v ? true : /^[0-9a-fA-F-]{36}$/.test(v)
      ),

    courtId: Yup.string()
      .nullable()
      .notRequired()
      .test("guid-or-empty", "Corte inválida.", (v) =>
        !v ? true : /^[0-9a-fA-F-]{36}$/.test(v)
      ),

    priorityId: Yup.string()
      .uuid("Prioridad inválida")
      .required("La prioridad es obligatoria."),

    externalCaseNumber: Yup.string()
      .nullable()
      .max(100, "Máximo 100 caracteres.")
      .test("trim", "Sin espacios al inicio/fin.", (v) =>
        typeof v === "string" ? v === v.trim() : true
      ),

    filingDate: Yup.date()
      .nullable()
      .typeError("Fecha de radicación inválida."),

    contactEmail: Yup.string()
      .nullable()
      .email("Correo de contacto inválido.")
      .max(200, "Máximo 200 caracteres."),

    notes: Yup.string().nullable().max(2000, "Máximo 2000 caracteres."),

    isDeadlineBasedCase: Yup.boolean().required(),

    responseDeadline: Yup.date()
      .nullable()
      .typeError("Fecha límite inválida.")
      .when("isDeadlineBasedCase", {
        is: true,
        then: (schema) => {
          let s = schema.required(
            "La fecha límite de respuesta es obligatoria."
          );

          // ✅ En creación: NO permitir fechas en el pasado
          if (isCreate) {
            s = s.test(
              "not-in-past",
              "La fecha límite no puede ser anterior a la fecha y hora actual.",
              (value) => {
                if (!value) return false;
                const now = new Date();
                return value >= now;
              }
            );
          }

          // ✅ En edición NO añadimos el test de "no puede ser menor que hoy"
          return s;
        },
        otherwise: (s) => s.nullable(),
      }),

    alertDaysBefore: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null ? null : value
      )
      .min(1, "Debe ser al menos 1 día de anticipación.")
      .max(60, "Máximo 60 días de anticipación.")
      .when("isDeadlineBasedCase", {
        is: true,
        then: (schema) =>
          schema.required(
            "Debes indicar cuántos días antes quieres la alerta."
          ),
        otherwise: (schema) => schema.nullable(),
      }),
  });

// ------------------------------------- Valores iniciales -------------------------------------
const initialValuesBase = {
  id: "",
  title: "",
  description: "",
  customerId: "",
  lawyerResponsibleId: "",
  caseTypeId: "",
  caseStatusId: "",
  jurisdictionId: "",
  courtId: "",
  priorityId: "",
  externalCaseNumber: "",
  filingDate: "",
  contactEmail: "",
  notes: "",
  isDeadlineBasedCase: false,
  responseDeadline: "",
  alertDaysBefore: 3,
};

// ------------------------------------- Secciones de formulario -------------------------------------
/**
 * Secciones colapsables (Accordion) para organizar el formulario.
 * - "basicos": título, descripción, clasificación principal.
 * - "partes": cliente y abogado responsable.
 * - "proceso": jurisdicción, corte, radicación, prioridad, Nº externo.
 * - "comunicacion": correo de contacto.
 * - "notas": observaciones adicionales.
 */
const SECTION_LIST = [
  { key: "basicos", label: "Datos básicos" },
  { key: "partes", label: "Partes" },
  { key: "proceso", label: "Proceso judicial" },
  { key: "comunicacion", label: "Comunicación" },
  { key: "notas", label: "Notas" },
];

// ------------------------------------- Page: CaseForm -------------------------------------
/**
 * Pantalla de Crear/Editar Caso.
 *
 * Características:
 * - Reutiliza patrón de CustomerForm (Formik + Yup + Accordion).
 * - StickyHeader con botón Volver, Guardar y barra de progreso.
 * - Se apoya en componentes reutilizables de selects (Category, Customer, Lawyer).
 */
const CaseForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/dashboard/cases";

  const { id } = useParams();
  const isCreate = !id;

  const [errors, setErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);

  const [caseTypeItems, setCaseTypeItems] = useState([]);

  //bandera para no repetir la carga del estado REGISTRADO
  const hasLoadedRegisteredStatus = useRef(false);

  // refs para scroll suave a secciones
  const sectionRefs = useRef(
    SECTION_LIST.reduce(
      (acc, s) => ({ ...acc, [s.key]: React.createRef() }),
      {}
    )
  );

  // estado acordeón (secciones abiertas)
  const [openKeys, setOpenKeys] = useState(["basicos", "partes"]);
  const toggleKey = (k) =>
    setOpenKeys((prev) =>
      prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]
    );
  const openAndScroll = (k) => {
    setOpenKeys((prev) => (prev.includes(k) ? prev : [...prev, k]));
    const el = sectionRefs.current[k]?.current;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const formik = useFormik({
    initialValues: initialValuesBase,
    validationSchema: buildValidationSchema(isCreate),
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setErrors([]);
      setSuccessMessage(null);

      try {
        // Normalizamos alertDaysBefore (mínimo 1, por defecto 3 si viene vacío)
        const alertDaysBefore =
          values.alertDaysBefore !== null &&
          values.alertDaysBefore !== undefined &&
          values.alertDaysBefore !== ""
            ? Number(values.alertDaysBefore)
            : 3;
        const payload = {
          id: isCreate ? undefined : id,
          title: values.title,
          description: values.description || "",
          customerId: values.customerId,
          lawyerResponsibleId: values.lawyerResponsibleId || null,
          caseTypeId: values.caseTypeId,
          caseStatusId: values.caseStatusId,
          jurisdictionId: values.jurisdictionId || null,
          courtId: values.courtId || null,
          priorityId: values.priorityId,
          externalCaseNumber: values.externalCaseNumber || "",
          filingDate: values.filingDate || null,
          contactEmail: values.contactEmail || "",
          notes: values.notes || "",
          isDeadlineBasedCase: values.isDeadlineBasedCase,
          responseDeadline: values.isDeadlineBasedCase
            ? values.responseDeadline || null
            : null,
          alertDaysBefore,
        };

        let resp;
        if (isCreate) resp = await createCase(payload);
        else resp = await updateCase(id, payload);

        if (!resp?.success) throw { backend: resp };

        setSuccessMessage(
          isCreate
            ? "✅ Caso creado correctamente."
            : "✅ Caso actualizado correctamente."
        );
        Swal.fire({
          toast: true,
          position: "top",
          icon: "success",
          title: isCreate ? "Caso creado" : "Cambios guardados",
          timer: 1800,
          showConfirmButton: false,
        });

        setTimeout(() => navigate("/dashboard/cases"), 1000);
      } catch (err) {
        setErrors(extractApiErrors(err));
      } finally {
        setSubmitting(false);
      }
    },
  });

  // --- Cargar estado REGISTRADO por defecto SOLO en creación ---
  useEffect(() => {
    if (!isCreate) return;

    // evitar ejecutar más de una vez aunque haya re-renders
    if (hasLoadedRegisteredStatus.current) return;

    const loadRegisteredStatus = async () => {
      try {
        // 1) Traer la categoría de estados de caso
        const resp = await getCategoryByCode(CASE_STATUS_CATEGORY_CODE);
        // resp = { success, data: { items: [...] } }

        if (resp?.success && Array.isArray(resp.data?.items)) {
          // 2) Buscar el item cuyo CODE = "REGISTRADO"
          const registeredItem = resp.data.items.find(
            (item) => item.code === "REGISTRADO"
          );

          if (registeredItem?.id) {
            // 3) Setear el valor en Formik
            formik.setFieldValue("caseStatusId", registeredItem.id, false);
            formik.setFieldTouched("caseStatusId", true, false);

            // marcar como ejecutado para no repetir
            hasLoadedRegisteredStatus.current = true;
          } else {
            console.warn(
              "No se encontró un CategoryItem con code = 'REGISTRADO' en case_status"
            );
          }
        } else {
          console.warn("Respuesta inválida al consultar case_status", resp);
        }
      } catch (err) {
        console.error("Error al obtener el estado REGISTRADO", err);
      }
    };

    loadRegisteredStatus();
  }, [isCreate]);

  // --- Cargar catálogo de tipos de caso (para saber su CODE) ---
  useEffect(() => {
    const loadCaseTypes = async () => {
      try {
        const resp = await getCategoryByCode(CASE_TYPE_CATEGORY_CODE);
        if (resp?.success && Array.isArray(resp.data?.items)) {
          setCaseTypeItems(resp.data.items);
        }
      } catch (err) {
        console.error("Error al cargar case_type", err);
      }
    };

    loadCaseTypes();
  }, []);

  // Cargar datos para edición
  useEffect(() => {
    if (isCreate) return;
    (async () => {
      try {
        const r = await getCaseById(id);
        if (!r?.success) throw new Error("No se pudo cargar el caso.");
        const d = r.data;

        formik.setValues({
          id: d.id,
          title: d.title || "",
          description: d.description || "",
          customerId: d.customerId || "",
          lawyerResponsibleId: d.lawyerResponsibleId || "",
          caseTypeId: d.caseTypeId || "",
          caseStatusId: d.caseStatusId || "",
          jurisdictionId: d.jurisdictionId || "",
          courtId: d.courtId || "",
          priorityId: d.priorityId || "",
          externalCaseNumber: d.externalCaseNumber || "",
          // filingDate llega en ISO; cortamos a 'yyyy-MM-ddTHH:mm' para datetime-local
          filingDate: d.filingDate ? d.filingDate.substring(0, 16) : "",
          contactEmail: d.contactEmail || "",
          notes: d.notes || "",
          isDeadlineBasedCase: d.isDeadlineBasedCase || false,
          responseDeadline: d.responseDeadline
            ? d.responseDeadline.substring(0, 16)
            : "",
          alertDaysBefore:
            typeof d.alertDaysBefore === "number" ? d.alertDaysBefore : 3,
        });
      } catch {
        Swal.fire("Error", "No se pudo cargar el caso.", "error");
        navigate("/dashboard/cases");
      }
    })();
  }, [id, isCreate]);

  // Hotkeys: Ctrl+S para guardar, Esc para volver
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        formik.handleSubmit();
      }
      if (e.key === "Escape") navigate("/dashboard/cases");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [formik, navigate]);

  // -------- Progreso de formulario --------
  const isFilled = (v) => {
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "string") return v.trim() !== "";
    return v !== null && v !== undefined && v !== "";
  };

  // Campos requeridos por sección
  const requiredBySection = useMemo(
    () => ({
      basicos: [
        "title",
        "description",
        "caseTypeId",
        "caseStatusId",
        "priorityId",
      ],
      partes: ["customerId", "lawyerResponsibleId"],
      proceso: [],
      comunicacion: [], // email es opcional
      notas: [],
    }),
    []
  );

  const nowForDateTimeLocal = React.useMemo(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const MM = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());

    // formato compatible con input[type="datetime-local"]
    return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
  }, []);

  const isPqrOrRightPetition = useMemo(() => {
    if (!formik.values.caseTypeId || caseTypeItems.length === 0) return false;

    const selected = caseTypeItems.find(
      (x) => x.id === formik.values.caseTypeId
    );
    if (!selected?.code) return false;

    const code = selected.code.toUpperCase();
    return code === "ADM_PQR" || code === "ADM_DER_PET";
  }, [formik.values.caseTypeId, caseTypeItems]);

  useEffect(() => {
    // Solo aplicamos lógica automática si ya tenemos el catálogo cargado
    if (!formik.values.caseTypeId) return;

    if (isPqrOrRightPetition) {
      // 1) Activar automáticamente la bandera de vencimiento
      if (!formik.values.isDeadlineBasedCase) {
        formik.setFieldValue("isDeadlineBasedCase", true, false);
      }

      // 2) Sugerir 5 días de anticipación SOLO si no hay un valor definido (>0)
      if (
        formik.values.alertDaysBefore === null ||
        formik.values.alertDaysBefore === "" ||
        formik.values.alertDaysBefore <= 0
      ) {
        formik.setFieldValue("alertDaysBefore", 5, false);
      }
    } else {
      // Al salir de PQR/DP no tocamos alertas, para no pisar lo que el usuario haya configurado.
      // Si quisieras resetear podrías hacerlo aquí.
    }
  }, [isPqrOrRightPetition, formik.values.caseTypeId]);

  useEffect(() => {
    if (isPqrOrRightPetition) {
      formik.setFieldValue("jurisdictionId", "");
      formik.setFieldValue("courtId", "");
    }
  }, [isPqrOrRightPetition]);

  const sectionIsComplete = (key) =>
    (requiredBySection[key] || []).every((path) => {
      const v = path.split(".").reduce((acc, p) => acc?.[p], formik.values);
      return isFilled(v);
    });

  const allRequired = Object.keys(requiredBySection).flatMap(
    (k) => requiredBySection[k]
  );
  const pendingCount = allRequired.filter((path) => {
    const v = path.split(".").reduce((acc, p) => acc?.[p], formik.values);
    return !isFilled(v);
  }).length;
  const progress = Math.round(
    (1 - pendingCount / (allRequired.length || 1)) * 100
  );

  const isInvalid = (name) => !!(formik.touched[name] && formik.errors[name]);
  const errorMsg = (name) => (formik.touched[name] ? formik.errors[name] : "");

  // ------------------------------------- RENDER -------------------------------------
  return (
    <>
      <StickyHeader
        title={isCreate ? "Crear caso" : "Editar caso"}
        onBack={() => navigate(from)}
        onSave={formik.handleSubmit}
        disabled={formik.isSubmitting}
        progress={progress}
      />

      <div className="container-fluid mt-4 px-4 w-100">
        <Row className="g-4">
          {/* Sidebar de secciones */}
          <Col lg={3}>
            <Card className="shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="fw-semibold">Secciones</div>
                  <Badge bg={pendingCount ? "warning" : "success"}>
                    {pendingCount ? `${pendingCount} pendientes` : "Completo"}
                  </Badge>
                </div>
                <div className="list-group list-group-flush">
                  {SECTION_LIST.map((s) => {
                    const complete = sectionIsComplete(s.key);
                    const open = openKeys.includes(s.key);
                    return (
                      <button
                        key={s.key}
                        type="button"
                        className={`list-group-item list-group-item-action d-flex align-items-center justify-content-between ${
                          open ? "active" : ""
                        }`}
                        onClick={() => openAndScroll(s.key)}
                      >
                        <span className="text-start">{s.label}</span>
                        {complete ? (
                          <CheckCircle2 size={18} className="text-success" />
                        ) : (
                          <CircleDot
                            size={16}
                            className="text-danger"
                            style={{ opacity: 0.7 }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 small text-muted d-flex align-items-center gap-2">
                  <ShieldCheck size={16} /> Puedes abrir múltiples secciones.
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Contenido principal */}
          <Col lg={9}>
            <Card className="shadow-sm">
              <Card.Body>
                <FormikProvider value={formik}>
                  <Form noValidate onSubmit={formik.handleSubmit}>
                    <Accordion
                      alwaysOpen
                      activeKey={openKeys}
                      onSelect={() => {}}
                    >
                      {/* BASICOS */}
                      <Accordion.Item
                        eventKey="basicos"
                        ref={sectionRefs.current.basicos}
                      >
                        <Accordion.Header onClick={() => toggleKey("basicos")}>
                          Datos básicos{" "}
                          {sectionIsComplete("basicos") && (
                            <Badge bg="success" className="ms-2">
                              Completa
                            </Badge>
                          )}
                        </Accordion.Header>
                        <Accordion.Body>
                          <Row className="g-3">
                            <Col md={12}>
                              <Form.Group controlId="title">
                                <Form.Label>Título del caso *</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="title"
                                  value={formik.values.title}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={
                                    formik.touched.title &&
                                    !!formik.errors.title
                                  }
                                  placeholder="Ej: Maltrato animal"
                                  maxLength={200}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {formik.errors.title}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            <Col md={12}>
                              <Form.Group controlId="description">
                                <Form.Label>Descripción *</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={4}
                                  name="description"
                                  value={formik.values.description}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={isInvalid("description")}
                                  placeholder="Describe brevemente el caso…"
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errorMsg("description")}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md={4}>
                              <Form.Group controlId="caseTypeId">
                                <Form.Label>Tipo de caso *</Form.Label>
                                <SelectCategory
                                  categoryCode={CASE_TYPE_CATEGORY_CODE}
                                  showCode={false}
                                  value={formik.values.caseTypeId}
                                  onChange={(val) =>
                                    formik.setFieldValue("caseTypeId", val)
                                  }
                                  placeholder="Seleccione tipo de caso"
                                />
                                {isInvalid("caseTypeId") && (
                                  <div className="invalid-feedback d-block">
                                    {errorMsg("caseTypeId")}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>

                            <Col md={4}>
                              <Form.Group controlId="caseStatusId">
                                <Form.Label>Estado *</Form.Label>
                                <SelectCategory
                                  categoryCode={CASE_STATUS_CATEGORY_CODE}
                                  showCode={false}
                                  value={formik.values.caseStatusId}
                                  onChange={(val) =>
                                    formik.setFieldValue("caseStatusId", val)
                                  }
                                  placeholder="Seleccione estado"
                                  isDisabled={isCreate}
                                />
                                {isInvalid("caseStatusId") && (
                                  <div className="invalid-feedback d-block">
                                    {errorMsg("caseStatusId")}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>

                            <Col md={4}>
                              <Form.Group controlId="priorityId">
                                <Form.Label>Prioridad *</Form.Label>
                                <SelectCategory
                                  categoryCode={CASE_PRIORITY_CATEGORY_CODE}
                                  showCode={false}
                                  value={formik.values.priorityId}
                                  onChange={(val) =>
                                    formik.setFieldValue("priorityId", val)
                                  }
                                  placeholder="Seleccione prioridad"
                                />
                                {isInvalid("priorityId") && (
                                  <div className="invalid-feedback d-block">
                                    {errorMsg("priorityId")}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* PARTES */}
                      <Accordion.Item
                        eventKey="partes"
                        ref={sectionRefs.current.partes}
                      >
                        <Accordion.Header onClick={() => toggleKey("partes")}>
                          Partes{" "}
                          {sectionIsComplete("partes") && (
                            <Badge bg="success" className="ms-2">
                              Completa
                            </Badge>
                          )}
                        </Accordion.Header>
                        <Accordion.Body>
                          <Row className="g-3">
                            <Col md={6}>
                              <Form.Group controlId="customerId">
                                <Form.Label>Cliente *</Form.Label>
                                <SelectCustomer
                                  value={formik.values.customerId}
                                  onChange={(val) =>
                                    formik.setFieldValue("customerId", val)
                                  }
                                  placeholder="Buscar y seleccionar cliente…"
                                />
                                {isInvalid("customerId") && (
                                  <div className="invalid-feedback d-block">
                                    {errorMsg("customerId")}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group controlId="lawyerResponsibleId">
                                <Form.Label>Abogado responsable *</Form.Label>
                                <SelectLawyer
                                  value={formik.values.lawyerResponsibleId}
                                  onChange={(val) =>
                                    formik.setFieldValue(
                                      "lawyerResponsibleId",
                                      val
                                    )
                                  }
                                  placeholder="Seleccionar abogado…"
                                  isClearable
                                />
                                {isInvalid("lawyerResponsibleId") && (
                                  <div className="invalid-feedback d-block">
                                    {errorMsg("lawyerResponsibleId")}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* PROCESO JUDICIAL */}
                      <Accordion.Item
                        eventKey="proceso"
                        ref={sectionRefs.current.proceso}
                      >
                        <Accordion.Header onClick={() => toggleKey("proceso")}>
                          Proceso judicial
                        </Accordion.Header>
                        <Accordion.Body>
                          {/* Chip informativo para PQR / Derecho de petición */}
                          {isPqrOrRightPetition && (
                            <div className="mb-3">
                              <div className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill bg-light border border-warning">
                                <AlertTriangle
                                  size={16}
                                  className="text-warning"
                                />
                                <small className="fw-semibold ">
                                  Este tipo de caso se gestiona por fecha límite
                                  de respuesta. Jurisdicción y Corte / Juzgado
                                  no aplican.
                                </small>
                              </div>
                            </div>
                          )}
                          <Row className="g-3">
                            {/* Solo mostramos Jurisdicción / Corte si NO es PQR ni Derecho de petición */}
                            {!isPqrOrRightPetition && (
                              <>
                                <Col md={6}>
                                  <Form.Group controlId="jurisdictionId">
                                    <Form.Label>Jurisdicción</Form.Label>
                                    <SelectCategory
                                      categoryCode={
                                        CASE_JURISDICTION_CATEGORY_CODE
                                      }
                                      showCode={false}
                                      value={formik.values.jurisdictionId}
                                      onChange={(val) =>
                                        formik.setFieldValue(
                                          "jurisdictionId",
                                          val
                                        )
                                      }
                                      placeholder="Seleccionar jurisdicción"
                                      isClearable
                                    />
                                    {isInvalid("jurisdictionId") && (
                                      <div className="invalid-feedback d-block">
                                        {errorMsg("jurisdictionId")}
                                      </div>
                                    )}
                                  </Form.Group>
                                </Col>

                                <Col md={6}>
                                  <Form.Group controlId="courtId">
                                    <Form.Label>Corte / Juzgado</Form.Label>
                                    <SelectCategory
                                      categoryCode={CASE_COURT_CATEGORY_CODE}
                                      showCode={false}
                                      value={formik.values.courtId}
                                      onChange={(val) =>
                                        formik.setFieldValue("courtId", val)
                                      }
                                      placeholder="Seleccionar corte"
                                      isClearable
                                    />
                                    {isInvalid("courtId") && (
                                      <div className="invalid-feedback d-block">
                                        {errorMsg("courtId")}
                                      </div>
                                    )}
                                  </Form.Group>
                                </Col>
                              </>
                            )}

                            <Col md={6}>
                              <Form.Group controlId="filingDate">
                                <Form.Label>Fecha de radicación</Form.Label>
                                <Form.Control
                                  type="datetime-local"
                                  name="filingDate"
                                  value={formik.values.filingDate || ""}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={
                                    formik.touched.filingDate &&
                                    !!formik.errors.filingDate
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {formik.errors.filingDate}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group controlId="externalCaseNumber">
                                <Form.Label>N° externo / radicado</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="externalCaseNumber"
                                  value={formik.values.externalCaseNumber}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={isInvalid("externalCaseNumber")}
                                  maxLength={100}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errorMsg("externalCaseNumber")}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                            {/* ALERTAS / VENCIMIENTO */}
                            <Col md={12} className="mt-3">
                              <Form.Group controlId="isDeadlineBasedCase">
                                <Form.Check
                                  type="switch"
                                  name="isDeadlineBasedCase"
                                  label="Este caso tiene fecha límite de respuesta"
                                  checked={formik.values.isDeadlineBasedCase}
                                  onChange={(e) =>
                                    formik.setFieldValue(
                                      "isDeadlineBasedCase",
                                      e.target.checked
                                    )
                                  }
                                />
                              </Form.Group>
                            </Col>

                            {formik.values.isDeadlineBasedCase && (
                              <>
                                <Col md={6}>
                                  <Form.Group controlId="responseDeadline">
                                    <Form.Label>
                                      Fecha límite de respuesta *
                                    </Form.Label>
                                    <Form.Control
                                      type="datetime-local"
                                      name="responseDeadline"
                                      min={nowForDateTimeLocal} // evita seleccionar pasado
                                      value={
                                        formik.values.responseDeadline || ""
                                      }
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                      isInvalid={isInvalid("responseDeadline")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {errorMsg("responseDeadline")}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Col>

                                <Col md={6}>
                                  <Form.Group controlId="alertDaysBefore">
                                    <Form.Label>
                                      Días de anticipación para la alerta *
                                    </Form.Label>
                                    <Form.Control
                                      type="number"
                                      min={1}
                                      max={60}
                                      name="alertDaysBefore"
                                      value={
                                        formik.values.alertDaysBefore !==
                                          null &&
                                        formik.values.alertDaysBefore !==
                                          undefined
                                          ? formik.values.alertDaysBefore
                                          : ""
                                      }
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                      isInvalid={isInvalid("alertDaysBefore")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {errorMsg("alertDaysBefore")}
                                    </Form.Control.Feedback>
                                    <Form.Text className="text-muted">
                                      Por defecto usamos 3 días si no indicas
                                      otro valor.
                                    </Form.Text>
                                  </Form.Group>
                                </Col>
                              </>
                            )}
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* COMUNICACIÓN */}
                      <Accordion.Item
                        eventKey="comunicacion"
                        ref={sectionRefs.current.comunicacion}
                      >
                        <Accordion.Header
                          onClick={() => toggleKey("comunicacion")}
                        >
                          Comunicación
                        </Accordion.Header>
                        <Accordion.Body>
                          <Row className="g-3">
                            <Col md={6}>
                              <Form.Group controlId="contactEmail">
                                <Form.Label>Correo de contacto</Form.Label>
                                <Form.Control
                                  type="email"
                                  name="contactEmail"
                                  value={formik.values.contactEmail}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={isInvalid("contactEmail")}
                                  placeholder="ejemplo@correo.com"
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errorMsg("contactEmail")}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* NOTAS */}
                      <Accordion.Item
                        eventKey="notas"
                        ref={sectionRefs.current.notas}
                      >
                        <Accordion.Header onClick={() => toggleKey("notas")}>
                          Notas
                        </Accordion.Header>
                        <Accordion.Body>
                          <Form.Group controlId="notes">
                            <Form.Label>Notas u observaciones</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={4}
                              name="notes"
                              value={formik.values.notes}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              isInvalid={isInvalid("notes")}
                              placeholder="Observaciones generales del caso…"
                            />
                            <Form.Control.Feedback type="invalid">
                              {errorMsg("notes")}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>

                    {/* Alertas backend */}
                    <div className="mt-3">
                      {errors.length > 0 && (
                        <AlertMessage
                          type="error"
                          message={
                            <ul className="mb-0">
                              {errors.map((e, i) => (
                                <li key={i}>{e}</li>
                              ))}
                            </ul>
                          }
                          onClose={() => setErrors([])}
                        />
                      )}
                      {successMessage && (
                        <AlertMessage
                          type="success"
                          message={successMessage}
                          onClose={() => setSuccessMessage(null)}
                        />
                      )}
                    </div>

                    {/* Botones (al pie) */}
                    <div className="d-flex justify-content-between mt-3">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate("/dashboard/cases")}
                      >
                        Volver
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={formik.isSubmitting}
                      >
                        {formik.isSubmitting
                          ? "Guardando..."
                          : isCreate
                          ? "Crear caso"
                          : "Actualizar caso"}
                      </button>
                    </div>
                  </Form>
                </FormikProvider>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default CaseForm;
